import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Phone, Instagram, Facebook, Linkedin, Youtube, ShoppingBag, Menu, X, ChevronDown, Search } from 'lucide-react';
import { CONTACT, SOCIALS, CATEGORIES } from '../mock';
import { brandIcon } from './BrandIcons';
import { useCart } from '../context/CartContext';
import logoHeader from '../assets/logo_header.webp';

const socialLucide = { instagram: Instagram, facebook: Facebook, linkedin: Linkedin, youtube: Youtube };

const TopStrip = () => (
  <div className="bg-gold-sheen text-[#231a08]">
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 flex items-center justify-between h-9 text-xs">
      <div className="flex items-center gap-4 font-medium">
        <a href={`tel:${CONTACT.phonePrimary.replace(/[^0-9]/g, '')}`} className="hidden sm:flex items-center gap-1.5 hover:opacity-70 transition-opacity">
          <Phone size={13} /> {CONTACT.phonePrimary}
        </a>
        <span className="hidden md:block w-px h-3.5 bg-[#231a08]/30" />
        <a href={`tel:${CONTACT.phoneSecondary.replace(/[^0-9]/g, '')}`} className="flex items-center gap-1.5 hover:opacity-70 transition-opacity">
          <Phone size={13} /> {CONTACT.phoneSecondary}
        </a>
      </div>
      <div className="flex items-center gap-3">
        {SOCIALS.map((s) => {
          const L = socialLucide[s.icon];
          return (
            <a key={s.name} href={s.url} aria-label={s.name} target="_blank" rel="noopener noreferrer"
              className="hover:opacity-60 transition-opacity">
              {L ? <L size={15} /> : brandIcon(s.icon, { width: 15, height: 15 })}
            </a>
          );
        })}
      </div>
    </div>
  </div>
);

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { count, setIsOpen } = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const navLinks = [
    { label: 'Home', to: '/' },
    { label: 'Shop', to: '/shop', hasDropdown: true },
    { label: 'About', to: '/about' },
    { label: 'Contact', to: '/contact' },
  ];

  return (
    <header className="sticky top-0 z-50">
      <TopStrip />
      <div className={`transition-all duration-500 border-b ${scrolled ? 'bg-[#0b0b0b]/95 backdrop-blur-md border-[#c6a15b]/20 py-3' : 'bg-[#0b0b0b] border-transparent py-5'}`}>
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 flex items-center justify-between relative">
          {/* Mobile menu btn */}
          <button className="lg:hidden text-[#e7d3a1]" onClick={() => setMobileOpen(true)} aria-label="Open menu">
            <Menu size={24} />
          </button>

          {/* Logo (image 2 — horizontal) left aligned, as large as the strip allows */}
          <Link to="/" className="flex items-center shrink-0">
            <img src={logoHeader} alt="Nikki's Next Level Hair and Beauty" className="h-11 sm:h-14 w-auto object-contain" />
          </Link>

          {/* Desktop nav — centered */}
          <nav className="hidden lg:flex items-center gap-9 lg:absolute lg:left-1/2 lg:-translate-x-1/2">
            {navLinks.map((link) => (
              <div key={link.label} className="relative"
                onMouseEnter={() => link.hasDropdown && setShopOpen(true)}
                onMouseLeave={() => link.hasDropdown && setShopOpen(false)}>
                <Link to={link.to}
                  data-active={location.pathname === link.to}
                  aria-current={location.pathname === link.to ? 'page' : undefined}
                  className={`nav-underline overline text-[0.72rem] flex items-center gap-1 transition-colors duration-300 ${location.pathname === link.to ? 'text-[#e7d3a1]' : 'text-[#cfc7b8] hover:text-[#e7d3a1]'}`}>
                  {link.label}
                  {link.hasDropdown && <ChevronDown size={13} className={`transition-transform ${shopOpen ? 'rotate-180' : ''}`} />}
                </Link>
                {link.hasDropdown && shopOpen && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 pt-4">
                    <div className="bg-[#111] border border-[#c6a15b]/25 shadow-2xl w-60 py-2 rise-in">
                      <Link to="/shop" className="block px-5 py-2.5 text-sm text-[#cfc7b8] hover:text-[#e7d3a1] hover:bg-white/5 font-body">All Products</Link>
                      <div className="gold-line my-1 mx-4" />
                      {CATEGORIES.map((c) => (
                        <Link key={c.slug} to={`/shop/${c.slug}`} className="block px-5 py-2.5 text-sm text-[#cfc7b8] hover:text-[#e7d3a1] hover:bg-white/5 font-body">{c.name}</Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-4 text-[#e7d3a1]">
            <button onClick={() => navigate('/shop')} aria-label="Search" className="hidden sm:block hover:opacity-70 transition-opacity">
              <Search size={20} />
            </button>
            <button onClick={() => setIsOpen(true)} aria-label="Cart" data-testid="header-cart-icon" className="relative hover:opacity-70 transition-opacity">
              <ShoppingBag size={22} />
              {count > 0 && (
                <motion.span
                  key={count}
                  initial={{ scale: 0.4 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 520, damping: 16 }}
                  data-testid="cart-badge"
                  className="absolute -top-2 -right-2 bg-gold-sheen text-[#231a08] text-[0.6rem] font-semibold w-4.5 h-4.5 min-w-[18px] h-[18px] rounded-full flex items-center justify-center">{count}</motion.span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile drawer */}
      <div className={`fixed inset-0 z-[60] lg:hidden transition-all duration-300 ${mobileOpen ? 'visible' : 'invisible'}`}>
        <div className={`absolute inset-0 bg-black/60 transition-opacity ${mobileOpen ? 'opacity-100' : 'opacity-0'}`} onClick={() => setMobileOpen(false)} />
        <div className={`absolute left-0 top-0 h-full w-[80%] max-w-xs bg-[#0b0b0b] border-r border-[#c6a15b]/20 p-6 transition-transform duration-400 ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}>
          <div className="flex items-center justify-between mb-8">
            <img src={logoHeader} alt="Nikki's Next Level Hair and Beauty" className="h-9 w-auto object-contain" />
            <button onClick={() => setMobileOpen(false)} className="text-[#e7d3a1]"><X size={24} /></button>
          </div>
          <nav className="flex flex-col gap-1">
            {navLinks.map((l) => (
              <Link key={l.label} to={l.to} className="py-3 border-b border-white/5 text-[#cfc7b8] hover:text-[#e7d3a1] overline text-sm">{l.label}</Link>
            ))}
            <div className="mt-3 pl-3 flex flex-col gap-1">
              {CATEGORIES.map((c) => (
                <Link key={c.slug} to={`/shop/${c.slug}`} className="py-2 text-sm text-[#9c9587] hover:text-[#e7d3a1] font-body">— {c.name}</Link>
              ))}
            </div>
          </nav>
          <div className="flex items-center gap-4 mt-8 text-[#e7d3a1]">
            {SOCIALS.map((s) => {
              const L = socialLucide[s.icon];
              return <a key={s.name} href={s.url} aria-label={s.name}>{L ? <L size={18} /> : brandIcon(s.icon)}</a>;
            })}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
